import { LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import { toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SoftwareType } from '../../../../domain/models/selectItems.model';
import { FileOutputFormularyModalSchemaKeys } from '../../useFileOutputFormularyModal.hook';
import { SelectorField } from '../SelectorField/SelectorField.ctrl';
import type { FieldsProps } from '../fieldsModel.model';

export function SoftwareNameField({
  form,
  options,
  isLoading,
  onFilterChange,
  versionFilterChange,
  moduleFilterChange,
}: FieldsProps): ReactNode {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE));

  const { watch, control, resetField } = form;

  const [softwareType, softwareNameId] = watch([
    FileOutputFormularyModalSchemaKeys.SoftwareTypeField,
    FileOutputFormularyModalSchemaKeys.SoftwareNameIdField,
  ]);

  const optionsIds = useMemo(() => {
    return options?.map(({ id }) => id) ?? [];
  }, [options]);

  const findOptionName = useCallback(
    (targetId: string) => {
      return options?.find(({ id }) => targetId === id)?.label ?? '';
    },
    [options],
  );

  const handleInputChange = useCallback(
    (evt: unknown, value: string, reason?: string) => {
      if (reason === 'clear' || reason === 'input') {
        onFilterChange(value);
      }
    },
    [onFilterChange],
  );

  const inputTextValue = useMemo(
    () => toString(options?.find((option) => option.id === softwareNameId)),
    [options, softwareNameId],
  );
  const clearNextFields = useCallback(() => {
    resetField('versionId');
    resetField('moduleId');
    if (versionFilterChange) {
      versionFilterChange('');
    }
    if (moduleFilterChange) {
      moduleFilterChange('');
    }
  }, [moduleFilterChange, resetField, versionFilterChange]);

  return (
    <SelectorField
      onChangeTrigger={clearNextFields}
      control={control}
      dataErrorTestId={'software-name-error-field-data-id'}
      dataTestId={'software-name-field-data-id'}
      fieldName={'softwareNameId'}
      getOptionLabel={(field) => findOptionName(field)}
      inputLabel={t('fileOutputModal.fields.software')}
      isDisabled={softwareType === SoftwareType.None}
      isLoading={isLoading}
      isReadOnly={softwareType === SoftwareType.None}
      options={optionsIds}
      onInputChange={handleInputChange}
      inputValue={inputTextValue}
    />
  );
}
